import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import UserIdentity from "@/components/UserIdentity";
import EmptyState from "@/components/EmptyState";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Search, Check, X, MoreVertical, Ban, UserMinus, Users, Inbox, Send, ShieldOff, MessageCircle, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";
import { useLanguage } from "@/context/LanguageContext";
import { logError } from "@/lib/log";

export default function FriendsPage() {
  const { language, t } = useLanguage();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "friends";
  const [friends, setFriends] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [followRequests, setFollowRequests] = useState([]);
  const [filter, setFilter] = useState("");
  const { subscribe } = useRealtime();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [f, inc, out, blk, incFollow] = await Promise.all([
        api.get("/friends"),
        api.get("/friends/requests/incoming"),
        api.get("/friends/requests/outgoing"),
        api.get("/blocks"),
        api.get("/follows/requests/incoming"),
      ]);
      setFriends(f.data);
      setIncoming(inc.data);
      setOutgoing(out.data);
      setBlocked(blk.data);
      setFollowRequests(incFollow.data);
    } catch (e) {
      logError("FriendsPage", e);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => subscribe((evt) => {
    if (["notification", "friend_request", "friend_accepted", "follow_request", "follow_accepted", "relationship_changed"].includes(evt.type)) load();
  }), [subscribe, load]);

  const act = async (fn, successMsg) => {
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const filtered = friends.filter((f) => f.username.toLowerCase().includes(filter.toLowerCase()));

  return (
    <AppShell>
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
          Friends
        </h1>
        <p className="mt-1 text-sm text-slate-500">Manage your connections, requests and blocked users.</p>
      </div>

      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })} className="w-full">
        <TabsList className="mb-4 h-auto rounded-full bg-slate-100 p-1">
          <TabsTrigger value="friends" data-testid="tab-friends" className="gap-1.5 rounded-full px-4 py-2 data-[state=active]:bg-white data-[state=active]:text-pink-600">
            <Users className="h-4 w-4" /> Friends
            {friends.length > 0 && <span className="ml-1 text-xs">{friends.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests" className="gap-1.5 rounded-full px-4 py-2 data-[state=active]:bg-white data-[state=active]:text-pink-600">
            <Inbox className="h-4 w-4" /> Requests
            {(incoming.length + followRequests.length) > 0 && (
              <span className="ml-1 rounded-full bg-pink-500 px-1.5 text-xs font-bold text-white">{incoming.length + followRequests.length}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="blocked" data-testid="tab-blocked" className="gap-1.5 rounded-full px-4 py-2 data-[state=active]:bg-white data-[state=active]:text-pink-600">
            <ShieldOff className="h-4 w-4" /> Blocked
          </TabsTrigger>
        </TabsList>

        {/* FRIENDS */}
        <TabsContent value="friends">
          <div className="relative mb-4">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={language === "ja" ? "友達を絞り込む…" : "Filter friends…"}
              data-testid="input-filter-friends"
              className="h-11 rounded-full border-slate-100 bg-white pl-11 shadow-sm"
            />
          </div>
          <Card className="rounded-3xl border-slate-100 shadow-sm">
            {filtered.length === 0 ? (
              <EmptyState
                icon={Users}
                title={t("No friends yet")}
                description={t("Head to Discover to find people and send your first friend request.")}
                testId="empty-state-friends"
              />
            ) : (
              <div className="divide-y divide-slate-50" data-testid="friends-list">
                {filtered.map((u) => (
                  <div key={u.user_id} className="flex items-center justify-between gap-3 p-4">
                    <UserIdentity user={u} onClick={() => navigate(`/u/${u.username}`)} />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100" data-testid={`friend-menu-${u.username}`}>
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-xl">
                        <DropdownMenuItem
                          onClick={() => navigate(`/messages?u=${u.username}`)}
                          data-testid={`btn-message-friend-${u.username}`}
                        >
                          <MessageCircle className="mr-2 h-4 w-4" /> Message
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => act(() => api.delete(`/friends/${u.user_id}`), `Removed @${u.username}`)}
                          data-testid={`btn-remove-friend-${u.username}`}
                        >
                          <UserMinus className="mr-2 h-4 w-4" /> Remove friend
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-red-500 focus:text-red-500"
                          onClick={() => act(() => api.post("/blocks", { username: u.username }), `Blocked @${u.username}`)}
                          data-testid={`btn-block-friend-${u.username}`}
                        >
                          <Ban className="mr-2 h-4 w-4" /> Block
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* REQUESTS */}
        <TabsContent value="requests" className="space-y-6">
          <div>
            <h2 className="mb-2 flex items-center gap-2 font-heading text-sm font-bold text-slate-700">
              <Inbox className="h-4 w-4 text-pink-500" /> Incoming
            </h2>
            <Card className="rounded-3xl border-slate-100 shadow-sm">
              {incoming.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-400">{t("No incoming requests.")}</p>
              ) : (
                <div className="divide-y divide-slate-50" data-testid="incoming-requests">
                  {incoming.map((r) => (
                    <div key={r.request_id} className="flex items-center justify-between gap-3 p-4">
                      <UserIdentity user={r.user} onClick={() => navigate(`/u/${r.user.username}`)} />
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => act(() => api.post(`/friends/requests/${r.request_id}/accept`), `You're now friends with @${r.user.username}`)}
                          data-testid={`btn-accept-request-${r.user.username}`}
                          className="gap-1 rounded-full bg-pink-500 font-semibold text-white hover:bg-pink-600"
                        >
                          <Check className="h-4 w-4" /> Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => act(() => api.post(`/friends/requests/${r.request_id}/decline`), "Request declined")}
                          data-testid={`btn-decline-request-${r.user.username}`}
                          className="gap-1 rounded-full font-semibold"
                        >
                          <X className="h-4 w-4" /> Decline
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          <div>
            <h2 className="mb-2 flex items-center gap-2 font-heading text-sm font-bold text-slate-700">
              <UserPlus className="h-4 w-4 text-pink-500" /> Follow requests
            </h2>
            <Card className="rounded-3xl border-slate-100 shadow-sm">
              {followRequests.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-400">{t("No follow requests.")}</p>
              ) : (
                <div className="divide-y divide-slate-50" data-testid="follow-requests">
                  {followRequests.map((r) => (
                    <div key={r.request_id} className="flex items-center justify-between gap-3 p-4">
                      <UserIdentity user={r.user} onClick={() => navigate("/u/" + r.user.username)} />
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => act(() => api.post("/follows/requests/" + r.request_id + "/accept"), "Follow request accepted")} className="gap-1 rounded-full bg-pink-500 font-semibold text-white hover:bg-pink-600"><Check className="h-4 w-4" /> Accept</Button>
                        <Button size="sm" variant="outline" onClick={() => act(() => api.post("/follows/requests/" + r.request_id + "/decline"), "Follow request declined")} className="gap-1 rounded-full font-semibold"><X className="h-4 w-4" /> Decline</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          <div>
            <h2 className="mb-2 flex items-center gap-2 font-heading text-sm font-bold text-slate-700">
              <Send className="h-4 w-4 text-pink-500" /> Sent
            </h2>
            <Card className="rounded-3xl border-slate-100 shadow-sm">
              {outgoing.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-400">No sent requests.</p>
              ) : (
                <div className="divide-y divide-slate-50" data-testid="outgoing-requests">
                  {outgoing.map((r) => (
                    <div key={r.request_id} className="flex items-center justify-between gap-3 p-4">
                      <UserIdentity user={r.user} />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => act(() => api.delete(`/friends/requests/${r.request_id}`), "Request cancelled")}
                        data-testid={`btn-cancel-request-${r.user.username}`}
                        className="rounded-full font-semibold"
                      >
                        Cancel
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* BLOCKED */}
        <TabsContent value="blocked">
          <Card className="rounded-3xl border-slate-100 shadow-sm">
            {blocked.length === 0 ? (
              <EmptyState
                icon={ShieldOff}
                title={t("No blocked users")}
                description={t("People you block won't be able to find you, message you, or send requests.")}
                testId="empty-state-blocked"
              />
            ) : (
              <div className="divide-y divide-slate-50" data-testid="blocked-list">
                {blocked.map((u) => (
                  <div key={u.user_id} className="flex items-center justify-between gap-3 p-4">
                    <UserIdentity user={u} showStatus={false} onClick={() => navigate(`/u/${u.username}`)} />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => act(() => api.delete(`/blocks/${u.user_id}`), `Unblocked @${u.username}`)}
                      data-testid={`btn-unblock-${u.username}`}
                      className="rounded-full font-semibold"
                    >
                      Unblock
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
